import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_Cell,
  type MRT_ColumnDef,
  type MRT_Row,
  type MRT_RowSelectionState,
  type MRT_TableInstance,
  type MRT_VisibilityState,
  MRT_TablePagination,
} from 'material-react-table';
import { Box, Card, Stack, Tooltip, Typography, MenuItem, Popover, IconButton, Menu, Badge, TextField } from '@mui/material';
import { toast } from 'react-toastify';
import { handleError } from '../../common/utils/util.errors';
import { capitalize, coRegex, differsWithBuffer, formatCurrency, parseCurrency, unescapeCSS } from '../../common/utils/common.util';
import { SUBTOTAL_ROW_BACKGROUND_COLOR } from '../../common/constants/general';
import { getColumnTypeFromKey, isNumericHeaderName, tryParseCurrencyLike } from '../../common/utils/column-type.util';
import { finalFieldsHeaders, payappProcessTooltips } from './Payapp.util';
import { usePayappProcess } from '../../context/PayappProcessContext';
import IconTooltip from '../common/IconTooltip';
import { Division } from '../glossary/glossary.type';
import SelectDivisionFromGiven from '../glossary/SelectDivisionFromGiven';
import { computeRangePool, getDefaultCheckedIds, toggleRangeItem } from '../project/subtotalRangeEdit.util';
import {
  Delete,
  Edit,
  AutoFixHigh,
  InfoOutlined,
  Add,
  BugReport,
  Build,
  Undo,
  Fullscreen,
  FullscreenExit,
  Search,
  MergeType,
  CheckCircleOutlined,
  Functions,
  Check,
  Close,
} from '@mui/icons-material';
import WhiteFab from '../common/WhiteFab';
import { fixPayappHeaders } from '../../common/utils/openai/openai.api';
import { useMRTStyling } from '../../hooks/useMRTStyling';
import { useExcelEditing, excelSelectedCellSx } from '../../hooks/useExcelEditing';
import { CustomManageColumnsMenu } from '../../components/common/CustomManageColumnsMenu';
import { IconOverTextButton } from '../common/IconOverTextButton';
import { ButtonDivider } from '../common/ButtonDivider';
import { ViewColumnOutlined as ViewColumnIcon, FilterList as FilterListIcon } from '@mui/icons-material';
import { GridFilterModel, GridLogicOperator, GridColDef } from '@mui/x-data-grid';
import { getStringOperators, getNumericOperators } from '../../common/utils/datagrid-sieve.util';
import FilterPanel from '../filter-panel/FilterPanel';
import { getAppliedFiltersCount } from '../filter-panel/filter-panel.util';
import { buildSelectAllCheckboxProps } from '../common/mrtSelectAllHelpers';
import AutoDeleteIcon from '@mui/icons-material/AutoDelete';
import MergeColumnsDialog from './MergeColumnsDialog';
import TruncatedCell from '../common/TruncatedCell';
import LegendItem from '../general/Legend';

type GuideStepKey =
  | 'fixHeaders'
  | 'selectSubtotals'
  | 'markSubtotals'
  | 'autoDelete'
  | 'deleteConfirm'
  | 'fixCurrency'
  | 'selectCO'
  | 'moveCO';

// Subtotals deliberately precede autoDelete: both are triggered by the same "row contains the word
// total" heuristic, so if Auto-Delete were highlighted first the guide would walk the user straight
// into deleting the very rows they were supposed to keep as subtotals.
const GUIDE_STEP_ORDER: GuideStepKey[] = [
  'fixHeaders',
  'selectSubtotals',
  'markSubtotals',
  'autoDelete',
  'deleteConfirm',
  'fixCurrency',
  'selectCO',
  'moveCO',
];

type RowData = {
  id: string;
  division?: Division;
  isChangeOrder?: boolean;
  isSubtotal?: boolean;
  // Overrides the default "since the previous subtotal" range with a fixed count of rows
  // (counting backward from this row, inclusive). null/undefined uses the default range.
  subtotalRange?: number | null;
  [key: string]: any;
};

// The row key holding the Description column, per the current header mapping. Undefined until the
// user has assigned a Description column.
const getDescriptionField = (fieldHeadersMapping: Record<string, string>): string | undefined =>
  Object.keys(fieldHeadersMapping).find(key => key === 'description' || fieldHeadersMapping[key] === 'Description');

// The single definition of "this row looks like a subtotal". Shared by selectSubtotalRows (which
// acts on it) and the hasUnmarkedSubtotalRows guide condition (which decides whether to highlight
// that button) — if the two ever disagreed, the guide could highlight Select Subtotals only for the
// click to report finding nothing.
const isSubtotalCandidate = (row: RowData, index: number, descriptionField: string): boolean => {
  // Already explicitly marked — skip.
  if (row.isSubtotal === true) return false;

  // The very first row can't be a subtotal — there's nothing above it to sum (see markAsSubtotal).
  if (index === 0) return false;

  return String(row[descriptionField] || '')
    .toLowerCase()
    .trim()
    .includes('total');
};

interface PayappTableProps {
  data: any[];
  projectId?: string;
  onSave?: (rows: Record<string, any>[], deleteExisting: boolean) => void;
  onTotalsChange?: (totals: Record<string, string>) => void;
  onSelectionChange?: (selectedIds: string[]) => void;
  fieldHeadersMapping: Record<string, string>;
  setFieldHeadersMapping: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  rows: RowData[];
  setRows: React.Dispatch<React.SetStateAction<RowData[]>>;
  onFixOCRIssues?: () => void;
}

const finalFields = finalFieldsHeaders.map(option => option.field);

const numericFinalHeaderNames = new Set<string>(finalFieldsHeaders.filter(f => f.headerName !== 'Description').map(f => f.headerName));

const tableHeaderToCoverFieldMap = {
  'Original Budget': 'Original Contract Budget Sum',
  Reallocation: 'Reallocations / Change Orders',
  'Change Order': 'Reallocations / Change Orders',
  'Revised Budget': 'Contract / Budget Sum to Date',
  'Completed Stored to Date': 'Total Completed and Stored',
  'Balance to Complete': 'Balance to Complete Plus Retainage',
  Retainage: 'Total Retainage Held',
} as Record<string, string>;

// Fix the PayappTable component
const PayappTable: React.FC<PayappTableProps> = ({
  data,
  onSave,
  onTotalsChange,
  onSelectionChange,
  fieldHeadersMapping,
  setFieldHeadersMapping,
  rows,
  setRows,
  onFixOCRIssues,
}) => {
  const initialFields = Object.keys(data[0] || {});
  const [columnVisibility, setColumnVisibility] = React.useState<MRT_VisibilityState>({ division: false });
  const [rowSelection, setRowSelection] = React.useState<MRT_RowSelectionState>({});
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  const [popoverAnchor, setPopoverAnchor] = useState<HTMLDivElement | null>(null);
  const [activeField, setActiveField] = useState<string | null>(null);
  const [columnsMenuAnchorEl, setColumnsMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLElement | null>(null);
  const [bulkDeleteInProgress, setBulkDeleteInProgress] = React.useState(false);
  const [columnOrder, setColumnOrder] = React.useState<string[]>([]);
  const [changeOrderSelectionActive, setChangeOrderSelectionActive] = useState(false);
  const [addedFields, setAddedFields] = useState<string[]>([]);
  const [filterModel, setFilterModel] = React.useState<GridFilterModel>({ items: [], logicOperator: GridLogicOperator.And });
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);
  const [hoveredRowPosition, setHoveredRowPosition] = useState<{ top: number } | null>(null);
  const [isButtonHovered, setIsButtonHovered] = useState(false);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const selectedCount = Object.keys(rowSelection).length;
  // Store information about the most recently deleted rows so we can undo the action.
  const [undoInfo, setUndoInfo] = useState<{ rows: RowData[]; indices: number[] } | null>(null);
  const tableInstanceRef = useRef<MRT_TableInstance<RowData> | null>(null);
  const tableContainerRef = useRef<HTMLDivElement | null>(null);
  const clearUndoInfo = () => setUndoInfo(null);

  // Step 2 should show plain numbers (no "$"), so default numeric blanks to "0".
  const zeroDisplay = useMemo(() => '0', []);

  const isAssignedHeader = useCallback((header?: string) => {
    return !!header && header !== '' && header !== 'Unassigned';
  }, []);

  const isNumericAssignedColumn = useCallback(
    (columnId: string) => {
      const header = fieldHeadersMapping[columnId];
      return isAssignedHeader(header) && numericFinalHeaderNames.has(header);
    },
    [fieldHeadersMapping, isAssignedHeader],
  );
  const insertChangeOrderIntoColumnOrder = (newField: string) => {
    setColumnOrder(prev => {
      // Start with current order or base fallback order
      const work = prev.length
        ? [...prev]
        : ['mrt-row-select', ...allFields.filter(f => f !== 'division' && f !== 'id'), 'mrt-row-actions'];

      // Remove if exists
      const idxExisting = work.indexOf(newField);
      if (idxExisting !== -1) work.splice(idxExisting, 1);

      // Determine positions relative to Original/Revised Budget
      const findIndexByHeaderIncludes = (keyword: string) =>
        work.findIndex(k => (fieldHeadersMapping[k] ?? '').toLowerCase().includes(keyword));

      let insertIdx = -1;
      const obIdx = findIndexByHeaderIncludes('original budget');
      const rbIdx = findIndexByHeaderIncludes('revised budget');
      if (obIdx !== -1) insertIdx = obIdx + 1;
      else if (rbIdx !== -1) insertIdx = rbIdx;
      else insertIdx = work.length - 1; // before actions column if present

      work.splice(insertIdx, 0, newField);
      return [...work];
    });
  };

  // Check if any CO rows exist that haven't been explicitly marked yet
  const hasChangeOrderRows = useMemo(() => {
    return rows.some(row => {
      if ((row as any).isChangeOrder === true) return false;
      return Object.entries(row)
        .filter(([key]) => key !== 'id' && key !== 'division')
        .some(([, v]) => coRegex.test(String(v ?? '')));
    });
  }, [rows]);
  const { commonMRTStyling, getRowStyling, getActionsColumnStyling } = useMRTStyling<any>();

  const {
    state,
    updateState,
    updateFieldHeadersMapping,
    setHeadersFixed,
    setFixingHeaders,
    setSelectedRowIds,
    getOCRIssueForCell,
    isCellFixed,
    getOCRIssuesCount,
  } = usePayappProcess();
  const ocrIssuesCount = getOCRIssuesCount();

  // Step guide state — initialise fixHeaders as already-done if headers were fixed in a prior visit
  const [completedSteps, setCompletedSteps] = useState<Set<GuideStepKey>>(() =>
    state.headersFixed ? new Set<GuideStepKey>(['fixHeaders']) : new Set<GuideStepKey>(),
  );
  const [autoDeleteTriggered, setAutoDeleteTriggered] = useState(false);
  const [selectSubtotalsTriggered, setSelectSubtotalsTriggered] = useState(false);
  const [selectCOTriggered, setSelectCOTriggered] = useState(false);
  const markStepComplete = useCallback(
    (step: GuideStepKey) =>
      setCompletedSteps(prev => {
        const next = new Set(prev);
        next.add(step);
        return next;
      }),
    [],
  );

  // ─── Step guide logic ────────────────────────────────────────────────────────

  const showFixHeadersCondition = useMemo(() => {
    const fieldKeys = new Set<string>(finalFieldsHeaders.map(f => f.field));
    const validHeaderNames = new Set<string>(finalFieldsHeaders.map(f => f.headerName));
    const allColumns = Object.keys(fieldHeadersMapping).filter(key => key !== 'id' && key !== 'division');
    if (allColumns.length === 0) return false;
    // A column counts as mapped if its value is a valid header name OR its key is already a finalField ID
    const mappedCount = allColumns.filter(key => validHeaderNames.has(fieldHeadersMapping[key]) || fieldKeys.has(key)).length;
    const unmappedRatio = (allColumns.length - mappedCount) / allColumns.length;
    return unmappedRatio >= 0.75;
  }, [fieldHeadersMapping]);

  const hasDeletableRows = useMemo(() => {
    return rows.some(row => {
      const values = Object.entries(row)
        .filter(([k]) => k !== 'id' && k !== 'division')
        .map(([, v]) =>
          String(v ?? '')
            .toLowerCase()
            .trim(),
        );
      if (values.every(v => !v)) return true;
      if (values.some(v => v.includes('total') || v.includes('subtotal'))) return true;
      const fields = Object.keys(row).filter(k => k !== 'id' && k !== 'division');
      if (fields.length > 0) {
        const matchingFields = fields.filter(
          k =>
            String(row[k] ?? '')
              .toLowerCase()
              .trim() === unescapeCSS(k.toLowerCase().trim()),
        );
        if ((matchingFields.length / fields.length) * 100 >= 70) return true;
      }
      const headerKeys =
        state.sourceTargetHeadersMapping && Object.keys(state.sourceTargetHeadersMapping).length > 0
          ? Object.keys(state.sourceTargetHeadersMapping)
          : Object.keys(fieldHeadersMapping).filter(k => k !== 'id' && k !== 'division');
      if (headerKeys.length > 0) {
        const matchingHeaders = headerKeys.filter(h => Object.values(row).some(v => String(v).toLowerCase().includes(h.toLowerCase())));
        if (matchingHeaders.length / headerKeys.length >= Math.max(0.8, (headerKeys.length - 2) / headerKeys.length)) return true;
      }
      return false;
    });
  }, [rows, fieldHeadersMapping, state.sourceTargetHeadersMapping]);

  const hasUnmarkedSubtotalRows = useMemo(() => {
    const descriptionField = getDescriptionField(fieldHeadersMapping);
    if (!descriptionField) return false;
    return rows.some((row, index) => isSubtotalCandidate(row, index, descriptionField));
  }, [rows, fieldHeadersMapping]);

  const stepConditions: Record<GuideStepKey, boolean> = useMemo(
    () => ({
      fixHeaders: showFixHeadersCondition,
      selectSubtotals: hasUnmarkedSubtotalRows,
      markSubtotals: selectSubtotalsTriggered && selectedCount > 0,
      autoDelete: hasDeletableRows,
      deleteConfirm: autoDeleteTriggered && selectedCount > 0,
      fixCurrency: ocrIssuesCount > 0 && !!onFixOCRIssues,
      selectCO: hasChangeOrderRows,
      moveCO: selectCOTriggered && selectedCount > 0,
    }),
    [
      showFixHeadersCondition,
      hasUnmarkedSubtotalRows,
      selectSubtotalsTriggered,
      hasDeletableRows,
      autoDeleteTriggered,
      selectedCount,
      ocrIssuesCount,
      onFixOCRIssues,
      hasChangeOrderRows,
      selectCOTriggered,
    ],
  );

  const activeStepKey = useMemo(
    () => GUIDE_STEP_ORDER.find(k => stepConditions[k] && !completedSteps.has(k)) ?? null,
    [stepConditions, completedSteps],
  );

  const stepMessages: Record<GuideStepKey, string> = useMemo(
    () => ({
      fixHeaders: 'Many column headers are unassigned — use Fix Headers to auto-assign them with AI.',
      selectSubtotals: 'Subtotal rows were detected — use Select Subtotals to find them before running Auto-Delete.',
      markSubtotals: `${selectedCount} row${selectedCount !== 1 ? 's' : ''} selected — click Mark as Subtotal to keep them out of Auto-Delete.`,
      autoDelete: 'Empty or total rows were detected — use Auto-Delete to select and remove them.',
      deleteConfirm: 'Review the selected rows, then click Delete to confirm removal.',
      fixCurrency: `${ocrIssuesCount} currency value${ocrIssuesCount !== 1 ? 's' : ''} could not be parsed — use Fix Currency to resolve them.`,
      selectCO: 'Change order rows were detected — use Select CO to identify and review them.',
      moveCO: `${selectedCount} change order row${selectedCount !== 1 ? 's' : ''} selected — use Move CO to reassign their budget amounts.`,
    }),
    [ocrIssuesCount, selectedCount],
  );

  // Clear selection-dependent triggers when user deselects all rows
  useEffect(() => {
    if (selectedCount === 0) {
      setAutoDeleteTriggered(false);
      setSelectSubtotalsTriggered(false);
      setSelectCOTriggered(false);
    }
  }, [selectedCount]);

  // ─────────────────────────────────────────────────────────────────────────────

  const isFieldHeaderMapped = (key: string) =>
    fieldHeadersMapping[key] && finalFieldsHeaders.some(lineItemField => lineItemField.headerName === fieldHeadersMapping[key]);

  // Handle row selection changes
  const handleRowSelection = (updatedSelection: MRT_RowSelectionState) => {
    setRowSelection(updatedSelection);
    if (onSelectionChange) {
      onSelectionChange(Object.keys(updatedSelection));
    }
  };

  // Handle column visibility changes
  const handleColumnVisibilityChange = (updatedVisibility: MRT_VisibilityState) => {
    setColumnVisibility(updatedVisibility);
  };

  // Handle bulk delete
  const handleBulkDelete = async () => {
    if (Object.keys(rowSelection).length === 0) {
      toast.error('No rows selected for deletion.');
      return;
    }
    setBulkDeleteInProgress(true);
    // Capture deleted rows & their indices for undo.
    const deletedRows: RowData[] = [];
    const deletedIndices: number[] = [];
    rows.forEach((item, idx) => {
      if (rowSelection[item.id]) {
        deletedRows.push(item);
        deletedIndices.push(idx);
      }
    });
    try {
      setRows(rows.filter((item: RowData) => !rowSelection[item.id]));
      setRowSelection({});
      setSelectedRowIds([]);
      setUndoInfo({ rows: deletedRows, indices: deletedIndices });
      markStepComplete('deleteConfirm');
      setAutoDeleteTriggered(false);
      toast.success('Selected rows deleted successfully.');
    } catch (error) {
      handleError(error);
    } finally {
      setBulkDeleteInProgress(false);
    }
  };

  // Handle single row delete
  const handleDeleteRow = (rowId: string) => {
    // Capture the deleted row for undo (with its index).
    const idx = rows.findIndex(r => r.id === rowId);
    if (idx !== -1) {
      setUndoInfo({ rows: [rows[idx]], indices: [idx] });
    }
    setRows((prevItems: RowData[]) => prevItems.filter((item: RowData) => item.id !== rowId));
    toast.success('Row deleted successfully.');
  };

  // Handle header click for field mapping
  const handleHeaderClick = (field: string, event: React.MouseEvent<HTMLDivElement>) => {
    setActiveField(field);
    setPopoverAnchor(event.currentTarget as HTMLDivElement);
  };

  useEffect(() => {
    const initializeHeaders = async () => {
      // Only auto-trigger fix headers if sourceTargetHeadersMapping is empty or null
      if (!state.headersFixed && (!state.sourceTargetHeadersMapping || Object.keys(state.sourceTargetHeadersMapping).length === 0)) {
        try {
          await handleFixHeaders();
          setHeadersFixed(true);
        } catch (error) {
          console.error('Error initializing headers:', error);
        }
      }
    };

    initializeHeaders();
  }, [state.headersFixed, state.sourceTargetHeadersMapping]); // Add sourceTargetHeadersMapping to dependencies

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  const handleFixHeaders = async () => {
    try {
      setFixingHeaders(true);
      const fieldsWithoutId = initialFields.filter(field => field !== 'id');
      const firstFewRowsData = rows.slice(0, 10).map(row => Object.values(row).map(val => String(val)));
      const newHeadersMapping = await fixPayappHeaders({ headers: fieldsWithoutId, rows: firstFewRowsData, targetHeaders: finalFields });

      if (Object.keys(newHeadersMapping).length === 0) {
        toast.error('Nothing to Update!');
        return;
      }

      for (const key in newHeadersMapping) {
        const newHeader = newHeadersMapping[key];
        if (finalFields.includes(newHeader)) {
          const finalFieldHeader = finalFieldsHeaders.find(fieldHeader => fieldHeader.field == newHeader);
          newHeadersMapping[key] = finalFieldHeader?.headerName || newHeader;
        } else {
          newHeadersMapping[key] = capitalize(newHeader);
        }
      }

      const newFieldHeadersMapping = { ...fieldHeadersMapping, ...newHeadersMapping };
      setFieldHeadersMapping(newFieldHeadersMapping);
      markStepComplete('fixHeaders');
    } catch (error) {
      handleError(error);
    } finally {
      setFixingHeaders(false);
    }
  };

  // Handle division change
  const handleDivisionChange = (rowId: string, divisionId: string | null) => {
    clearUndoInfo();
    const division = state.divisions.find(d => d.id === divisionId) || null;

    setRows((prevRows: RowData[]) =>
      prevRows.map((row: RowData) => {
        if (row.id === rowId) {
          return {
            ...row,
            division,
          };
        }
        return row;
      }),
    );

    return division;
  };

  // Add missing functionality from original
  const selectDeletableRows = useCallback(() => {
    const descriptionField = getDescriptionField(fieldHeadersMapping);

    if (!descriptionField) {
      toast.error('Please assign the Description column first!');
      return;
    }

    const deletableRows = rows.filter(row => {
      // Rows explicitly marked as subtotals should never be auto-deleted.
      if (row.isSubtotal === true) return false;

      const description = String(row[descriptionField] || '')
        .toLowerCase()
        .trim();

      const isEmpty = !description;
      // Check for total-like descriptions
      const isTotalLike = description.includes('subtotal') || description.includes('total') || description.includes('grand total');

      // Check if column fields match their values with 70% threshold
      const fields = Object.keys(row).filter(key => key !== 'id'); // Skip id field
      const isHeaderLike =
        fields.length > 0 &&
        (() => {
          const matchingFields = fields.filter(key => {
            const value = String(row[key] || '')
              .toLowerCase()
              .trim();
            const fieldName = unescapeCSS(key.toLowerCase().trim());
            return value === fieldName;
          });
          return (matchingFields.length / fields.length) * 100 >= 70;
        })();

      // Check if row contains majority of original headers from sourceTargetHeadersMapping
      let containsMajorityOfHeaders = false;
      if (state.sourceTargetHeadersMapping && Object.keys(state.sourceTargetHeadersMapping).length > 0) {
        // Use sourceTargetHeadersMapping keys (original headers)
        const originalHeaders = Object.keys(state.sourceTargetHeadersMapping);
        const matchingHeaders = originalHeaders.filter(originalHeader =>
          // Check if ANY row value contains this original header
          Object.values(row).some(value => String(value).toLowerCase().includes(originalHeader.toLowerCase())),
        );
        // 80% threshold with buffer of 1-2 headers
        const threshold = Math.max(0.8, (originalHeaders.length - 2) / originalHeaders.length);
        containsMajorityOfHeaders = matchingHeaders.length / originalHeaders.length >= threshold;
      } else {
        // Fallback to fieldHeadersMapping keys when sourceTargetHeadersMapping is empty
        const fieldKeys = Object.keys(fieldHeadersMapping);
        const matchingFieldKeys = fieldKeys.filter(fieldKey =>
          Object.values(row).some(value => String(value).toLowerCase().includes(fieldKey.toLowerCase())),
        );
        // 80% threshold with buffer of 1-2 headers
        const threshold = Math.max(0.8, (fieldKeys.length - 2) / fieldKeys.length);
        containsMajorityOfHeaders = matchingFieldKeys.length / fieldKeys.length >= threshold;
      }
      return isEmpty || isTotalLike || isHeaderLike || containsMajorityOfHeaders;
    });

    if (deletableRows.length === 0) {
      toast.info('No deletable rows found!');
      return;
    }

    const deletableIds = deletableRows.map(row => row.id);
    setRowSelection(deletableIds.reduce((acc, id) => ({ ...acc, [id]: true }), {}));
    setSelectedRowIds(deletableIds);
    markStepComplete('autoDelete');
    setAutoDeleteTriggered(true);
    toast.success(`Found ${deletableIds.length} rows that can be deleted!`);
  }, [rows, fieldHeadersMapping, setSelectedRowIds, handleRowSelection, state.sourceTargetHeadersMapping]);

  // Select rows that look like subtotals (contain "total" anywhere in the Description column)
  // and haven't already been marked as a subtotal. Mirrors selectDeletableRows/selectChangeOrderRows,
  // but only selects — marking happens as a separate, explicit step via markAsSubtotal so the user
  // can review the candidates first.
  const selectSubtotalRows = useCallback(() => {
    const descriptionField = getDescriptionField(fieldHeadersMapping);

    if (!descriptionField) {
      toast.error('Please assign the Description column first!');
      return;
    }

    const subtotalCandidates = rows.filter((row, index) => isSubtotalCandidate(row, index, descriptionField));

    if (subtotalCandidates.length === 0) {
      toast.info('No subtotal-like rows found!');
      return;
    }

    const ids = subtotalCandidates.map(row => row.id);
    setRowSelection(ids.reduce((acc, id) => ({ ...acc, [id]: true }), {}));
    setSelectedRowIds(ids);
    markStepComplete('selectSubtotals');
    setSelectSubtotalsTriggered(true);
    toast.success(`Selected ${ids.length} row(s) that look like subtotals!`);
  }, [rows, fieldHeadersMapping, setSelectedRowIds, markStepComplete]);

  // Mark the currently selected rows as subtotals. Marked rows are excluded from Auto-Delete
  // (see selectDeletableRows) and are visually distinguished in the Description column.
  const markAsSubtotal = useCallback(() => {
    const selectedIds = Object.keys(rowSelection).filter(id => rowSelection[id]);
    if (selectedIds.length === 0) {
      toast.info('Please select some rows first.');
      return;
    }

    // A subtotal has to sum something, so it can never be the top row — at least one real (non-subtotal)
    // line item must sit above it. This mirrors the Step 2→3 transition (PayappProcessFooter.tsx), which
    // anchors each subtotal to the nearest real row above it and silently DROPS any subtotal with no such
    // row; rejecting it here makes that visible up front instead of losing it later.
    const selected = new Set(selectedIds);
    const eligibleIds: string[] = [];
    let hasRealRowAbove = false;
    rows.forEach(row => {
      const willBeSubtotal = selected.has(row.id) || row.isSubtotal === true;
      if (selected.has(row.id) && hasRealRowAbove) eligibleIds.push(row.id);
      if (!willBeSubtotal) hasRealRowAbove = true;
    });

    if (eligibleIds.length === 0) {
      toast.error('A subtotal needs at least one line item above it, so the top row cannot be marked as a subtotal.');
      return;
    }

    const skippedCount = selectedIds.length - eligibleIds.length;
    const eligible = new Set(eligibleIds);
    setRows(prev => prev.map(row => (eligible.has(row.id) ? { ...row, isSubtotal: true } : row)));
    setRowSelection({});
    setSelectedRowIds([]);
    markStepComplete('markSubtotals');
    setSelectSubtotalsTriggered(false);
    toast.success(`Marked ${eligibleIds.length} row(s) as subtotal(s).`);
    if (skippedCount > 0) {
      toast.warn(`Skipped ${skippedCount} row(s) with no line item above them to sum.`);
    }
  }, [rows, rowSelection, setRows, setSelectedRowIds, markStepComplete]);

  // Checkbox-based range editor for a subtotal row: which rows above it (counting backward,
  // exclusive of the subtotal row itself) it sums, instead of a numeric-entry dialog. Since Step 2
  // rows aren't DrawLineItem entities yet, there's no anchor FK — the "anchor" here is just the
  // nearest non-subtotal row above the subtotal, and the subtotal row itself physically moves in
  // `rows` when that anchor changes (see confirmRangeEdit below).
  const findAnchorForSubtotalRow = useCallback((allRows: RowData[], subtotalIndex: number): string | null => {
    for (let i = subtotalIndex - 1; i >= 0; i--) {
      if (!allRows[i].isSubtotal) return allRows[i].id;
    }
    return null;
  }, []);

  const [rangeEditSubtotalId, setRangeEditSubtotalId] = useState<string | null>(null);
  const [rangeEditPoolIds, setRangeEditPoolIds] = useState<string[]>([]);
  const [rangeEditPoolStartIndex, setRangeEditPoolStartIndex] = useState(0);
  const [rangeEditAnchorId, setRangeEditAnchorId] = useState<string>('');
  const [rangeEditCheckedIds, setRangeEditCheckedIds] = useState<Set<string>>(new Set());
  const [rangeEditOtherAnchors, setRangeEditOtherAnchors] = useState<Map<string, string | null>>(new Map());

  const handleStartRangeEdit = useCallback(
    (subtotalRowId: string) => {
      const subtotalIndex = rows.findIndex(r => r.id === subtotalRowId);
      if (subtotalIndex < 0) return;

      const thisAnchorId = findAnchorForSubtotalRow(rows, subtotalIndex);
      if (!thisAnchorId) {
        toast.error('There are no line items above this subtotal to select.');
        return;
      }

      const nonSubtotalIds = rows.filter(r => !r.isSubtotal).map(r => r.id);
      const subtotalRows = rows.filter(r => r.isSubtotal);
      const idxAmongSubtotals = subtotalRows.findIndex(r => r.id === subtotalRowId);
      const previousSubtotal = idxAmongSubtotals > 0 ? subtotalRows[idxAmongSubtotals - 1] : null;
      const nextSubtotal = idxAmongSubtotals < subtotalRows.length - 1 ? subtotalRows[idxAmongSubtotals + 1] : null;
      const previousAnchorId = previousSubtotal
        ? findAnchorForSubtotalRow(
            rows,
            rows.findIndex(r => r.id === previousSubtotal.id),
          )
        : null;
      const nextAnchorId = nextSubtotal
        ? findAnchorForSubtotalRow(
            rows,
            rows.findIndex(r => r.id === nextSubtotal.id),
          )
        : null;

      const startIdx = previousAnchorId ? nonSubtotalIds.indexOf(previousAnchorId) + 1 : 0;
      const pool = computeRangePool(nonSubtotalIds, previousAnchorId, nextAnchorId);
      const subtotalRow = rows[subtotalIndex];
      const checked = getDefaultCheckedIds(pool, thisAnchorId, subtotalRow.subtotalRange);

      const otherAnchors = new Map<string, string | null>();
      subtotalRows.forEach(sr => {
        if (sr.id === subtotalRowId) return;
        otherAnchors.set(
          sr.id,
          findAnchorForSubtotalRow(
            rows,
            rows.findIndex(r => r.id === sr.id),
          ),
        );
      });

      setRangeEditSubtotalId(subtotalRowId);
      setRangeEditPoolIds(pool);
      setRangeEditPoolStartIndex(Math.max(0, startIdx));
      setRangeEditAnchorId(thisAnchorId);
      setRangeEditCheckedIds(checked);
      setRangeEditOtherAnchors(otherAnchors);
      updateState({ subtotalRangeEditActive: true });
    },
    [rows, findAnchorForSubtotalRow, updateState],
  );

  const handleToggleRangeItem = useCallback(
    (itemId: string, checked: boolean) => {
      const result = toggleRangeItem(rangeEditPoolIds, rangeEditAnchorId, rangeEditCheckedIds, itemId, checked);
      if (!result) {
        toast.error(checked ? 'Unable to include this line item in the subtotal.' : 'A subtotal must include at least one line item.');
        return;
      }
      setRangeEditPoolIds(result.poolIds);
      setRangeEditAnchorId(result.anchorId);
      setRangeEditCheckedIds(result.checkedIds);
    },
    [rangeEditPoolIds, rangeEditAnchorId, rangeEditCheckedIds],
  );

  const handleCancelRangeEdit = useCallback(() => {
    setRangeEditSubtotalId(null);
    setRangeEditPoolIds([]);
    setRangeEditCheckedIds(new Set());
    setRangeEditAnchorId('');
    setRangeEditOtherAnchors(new Map());
    updateState({ subtotalRangeEditActive: false });
  }, [updateState]);

  // Shared by the live preview (below) and handleConfirmRangeEdit — reconstructs the full `rows`
  // array with the pool's in-progress order spliced in and the edited subtotal row repositioned
  // to sit right after its (possibly new) anchor.
  const computeReorderedRows = useCallback((): RowData[] => {
    if (!rangeEditSubtotalId) return rows;

    const nonSubtotalIds = rows.filter(r => !r.isSubtotal).map(r => r.id);
    const nextNonSubtotalIds = nonSubtotalIds.slice();
    nextNonSubtotalIds.splice(rangeEditPoolStartIndex, rangeEditPoolIds.length, ...rangeEditPoolIds);

    const rowById = new Map(rows.map(r => [r.id, r]));
    const nextNonSubtotalRows = nextNonSubtotalIds.map(id => rowById.get(id)).filter((r): r is RowData => !!r);

    const subtotalAnchors = new Map(rangeEditOtherAnchors);
    subtotalAnchors.set(rangeEditSubtotalId, rangeEditAnchorId);

    const editedSubtotalRow: RowData = { ...(rowById.get(rangeEditSubtotalId) as RowData), subtotalRange: rangeEditCheckedIds.size };

    const finalRows: RowData[] = [];
    nextNonSubtotalRows.forEach(r => {
      finalRows.push(r);
      subtotalAnchors.forEach((subAnchorId, subId) => {
        if (subAnchorId === r.id) {
          finalRows.push(subId === rangeEditSubtotalId ? editedSubtotalRow : (rowById.get(subId) as RowData));
        }
      });
    });
    // Any subtotal with no row above it (shouldn't normally happen, guarded in handleStartRangeEdit)
    // gets prepended so it isn't silently dropped.
    subtotalAnchors.forEach((subAnchorId, subId) => {
      if (subAnchorId === null) {
        finalRows.unshift(subId === rangeEditSubtotalId ? editedSubtotalRow : (rowById.get(subId) as RowData));
      }
    });

    return finalRows;
  }, [rangeEditSubtotalId, rangeEditPoolIds, rangeEditPoolStartIndex, rangeEditAnchorId, rangeEditCheckedIds, rangeEditOtherAnchors, rows]);

  // Live preview of the reorder-in-progress, rendered immediately as the user checks/unchecks
  // rows, rather than only after Confirm.
  const previewRows = React.useMemo(
    () => (rangeEditSubtotalId ? computeReorderedRows() : null),
    // eslint-disable-next-line
    [rangeEditSubtotalId, rangeEditPoolIds, rangeEditAnchorId, rangeEditCheckedIds],
  );

  const handleConfirmRangeEdit = useCallback(() => {
    if (!rangeEditSubtotalId) return;
    setRows(computeReorderedRows());
    handleCancelRangeEdit();
  }, [rangeEditSubtotalId, computeReorderedRows, setRows, handleCancelRangeEdit]);

  const rangeEditSelectionState = React.useMemo(() => {
    const state: MRT_RowSelectionState = {};
    rangeEditCheckedIds.forEach(id => {
      state[id] = true;
    });
    return state;
  }, [rangeEditCheckedIds]);

  const handleRangeAwareRowSelectionChange = useCallback(
    (updatedSelectionOrUpdater: MRT_RowSelectionState | ((old: MRT_RowSelectionState) => MRT_RowSelectionState)) => {
      if (rangeEditSubtotalId) {
        const updatedSelection =
          typeof updatedSelectionOrUpdater === 'function' ? updatedSelectionOrUpdater(rangeEditSelectionState) : updatedSelectionOrUpdater;
        const prevKeys = new Set(Object.keys(rangeEditSelectionState).filter(k => rangeEditSelectionState[k]));
        const nextKeys = new Set(Object.keys(updatedSelection).filter(k => updatedSelection[k]));
        for (const id of Array.from(nextKeys)) {
          if (!prevKeys.has(id)) {
            handleToggleRangeItem(id, true);
            return;
          }
        }
        for (const id of Array.from(prevKeys)) {
          if (!nextKeys.has(id)) {
            handleToggleRangeItem(id, false);
            return;
          }
        }
        return;
      }
      handleRowSelection(updatedSelectionOrUpdater as MRT_RowSelectionState);
    },
    [rangeEditSubtotalId, rangeEditSelectionState, handleToggleRangeItem],
  );

  // For each row marked as a subtotal, compute — per numeric column — the sum of every row
  // since the previous subtotal (exclusive) up to (but not including) this one. This is the
  // "sum of line items above, since the last subtotal" rule. If the payapp itself printed a
  // non-zero value in that same cell and it meaningfully differs from the computed sum, that's
  // flagged as a mismatch so the cell can be highlighted with the payapp's original value shown
  // in a tooltip.
  const subtotalColumnInfoByRow = useMemo(() => {
    const numericKeys = Object.keys(fieldHeadersMapping).filter(key => isNumericAssignedColumn(key));
    const result: Record<string, Record<string, { computedSum: number; payappValue: number | null; mismatch: boolean }>> = {};

    let rangeStart = 0;
    rows.forEach((row, index) => {
      if (!row.isSubtotal) return;

      let rangeRows = rows.slice(rangeStart, index);
      // An explicit range override takes the last N rows of the default range (counting backward
      // from this row, inclusive), which naturally clamps to the previous subtotal boundary for
      // free, since rangeRows is already bounded by that default range.
      if (row.subtotalRange != null && row.subtotalRange < rangeRows.length) {
        rangeRows = rangeRows.slice(rangeRows.length - row.subtotalRange);
      }
      const columnInfo: Record<string, { computedSum: number; payappValue: number | null; mismatch: boolean }> = {};

      numericKeys.forEach(key => {
        const computedSum = rangeRows.reduce((acc, r) => {
          const parsed = tryParseCurrencyLike(r[key]);
          return parsed === null ? acc : acc + parsed;
        }, 0);
        const payappValue = tryParseCurrencyLike(row[key]);
        // Only flag a mismatch when the payapp actually printed a value for this cell — a blank
        // cell just means the payapp didn't provide a figure for that column, not a disagreement.
        const mismatch = payappValue !== null && payappValue !== 0 && differsWithBuffer(computedSum, payappValue, 1.0);
        columnInfo[key] = { computedSum, payappValue, mismatch };
      });

      result[row.id] = columnInfo;
      rangeStart = index + 1;
    });

    return result;
  }, [rows, fieldHeadersMapping, isNumericAssignedColumn]);

  // When we add a row at the bottom, we want to scroll to it (Payapp Step 2 UX)
  const pendingScrollToEndRef = useRef(false);
  const pendingScrollToEndAfterPaginationRef = useRef(false);

  const handleAddNewRow = (insertIndex?: number) => {
    clearUndoInfo();
    const id = `new-${Date.now()}`;
    const newRow: RowData = { id: id };
    // Add empty values for all fields except id
    Object.keys(data[0] || {}).forEach(key => {
      if (key !== 'id') {
        (newRow as any)[key] = isNumericAssignedColumn(key) ? zeroDisplay : '';
      }
    });

    if (insertIndex !== undefined) {
      // Insert at specific index
      setRows(prevRows => {
        const newRows = [...prevRows];
        newRows.splice(insertIndex, 0, newRow);
        return newRows;
      });
    } else {
      // Default: add at the bottom
      pendingScrollToEndRef.current = true;
      setRows(prevRows => [...prevRows, newRow]);
    }
  };

  // When rows are loaded (or when mapping changes), ensure numeric mapped columns never contain empty values.
  // This keeps downstream parsing consistent (and avoids treating "missing" differently than 0),
  // and strips OCR-introduced ":" artifacts that sometimes appear in numeric cells.
  useEffect(() => {
    if (!rows?.length) return;

    const numericMappedKeys = Object.keys(fieldHeadersMapping).filter(key => isNumericAssignedColumn(key));
    if (numericMappedKeys.length === 0) return;

    let didChange = false;
    const nextRows = rows.map(r => {
      let next: RowData | null = null;
      for (const key of numericMappedKeys) {
        const current = (next ?? r)[key];
        if (current === '' || current === null || current === undefined) {
          if (!next) next = { ...r };
          (next as any)[key] = zeroDisplay;
          didChange = true;
          continue;
        }

        if (typeof current === 'string' && current.includes(':')) {
          const cleaned = current.replace(/:/g, '').trim();
          if (cleaned !== current) {
            if (!next) next = { ...r };
            (next as any)[key] = cleaned;
            didChange = true;
          }
        }
      }
      return next ?? r;
    });

    if (didChange) setRows(nextRows);
  }, [rows.length, fieldHeadersMapping, isNumericAssignedColumn, zeroDisplay, setRows]);

  // Ensure columns remain visible even when there are no data rows by also including keys from fieldHeadersMapping
  const allFields = useMemo(() => {
    const keysFromData = data.length > 0 ? Object.keys(data[0]) : [];
    const keysFromMapping = Object.keys(fieldHeadersMapping);
    return Array.from(new Set([...keysFromData, ...keysFromMapping, ...addedFields]));
  }, [data, fieldHeadersMapping, addedFields]);

  // Define filterable columns based on field mappings
  const filterColumns: GridColDef[] = useMemo(() => {
    return allFields
      .filter(field => field !== 'id' && field !== 'division')
      .map(field => {
        const headerName = fieldHeadersMapping[field] || field;
        // Determine type based on header name or field content
        const isNumericField = isNumericHeaderName(headerName);

        return {
          field,
          headerName,
          type: isNumericField ? 'number' : 'string',
          filterOperators: isNumericField ? getNumericOperators() : getStringOperators(),
        };
      });
  }, [allFields, fieldHeadersMapping]);

  const skippedFields: string[] = ['id', 'division'];

  // Apply filters to rows
  const filteredRows = useMemo(() => {
    if (!filterModel || !filterModel.items || filterModel.items.length === 0) {
      return rows;
    }

    return rows.filter((row: RowData) => {
      return filterModel.items.every(filterItem => {
        const { field, operator, value } = filterItem;
        if (!value && value !== 0) return true;

        const fieldValue = row[field];
        if (fieldValue === undefined || fieldValue === null) return false;

        // Convert to string for string comparisons
        const fieldValueStr = String(fieldValue).toLowerCase();
        const valueStr = String(value).toLowerCase();

        switch (operator) {
          case 'contains':
            return fieldValueStr.includes(valueStr);
          case 'equals':
            return fieldValue === value || fieldValueStr === valueStr;
          case 'startsWith':
            return fieldValueStr.startsWith(valueStr);
          case 'endsWith':
            return fieldValueStr.endsWith(valueStr);
          case 'isEmpty':
            return !fieldValue || fieldValueStr === '';
          case 'isNotEmpty':
            return fieldValue && fieldValueStr !== '';
          case '>':
            return parseCurrency(fieldValue as any) > parseCurrency(value as any);
          case '>=':
            return parseCurrency(fieldValue as any) >= parseCurrency(value as any);
          case '<':
            return parseCurrency(fieldValue as any) < parseCurrency(value as any);
          case '<=':
            return parseCurrency(fieldValue as any) <= parseCurrency(value as any);
          case '=':
            return parseCurrency(fieldValue as any) === parseCurrency(value as any);
          case '!=':
            return parseCurrency(fieldValue as any) !== parseCurrency(value as any);
          default:
            return true;
        }
      });
    });
  }, [rows, filterModel]);

  // Select rows that look like Change Orders (contain "CO" somewhere) and haven't been
  // explicitly marked as change orders yet (isChangeOrder !== true).
  // We intentionally do NOT filter out rows that already have a non-zero Change Order /
  // Reallocation column value — those rows still need the isChangeOrder=true label so
  // the downstream merge assigns the LineItemLabelEnum.ChangeOrder tag to them.
  const selectChangeOrderRows = useCallback(() => {
    // Match any occurrence of "CO" that is NOT immediately followed by an alphabetic letter
    // Examples that match: CO, CO#, CO-1, CO 2, CO), ACO#1, 123CO
    // Examples that do NOT match: COlumn, Color, account

    const changeOrderCandidates = rows.filter(row => {
      // Already explicitly marked — skip
      if ((row as any).isChangeOrder === true) return false;

      // Contains CO pattern in any column value (except id / division)
      return Object.entries(row)
        .filter(([key]) => key !== 'id' && key !== 'division')
        .some(([, v]) => coRegex.test(String(v ?? '')));
    });

    if (changeOrderCandidates.length === 0) {
      toast.info('No change order rows found!');
      return;
    }

    const ids = changeOrderCandidates.map(r => r.id);
    const selection = ids.reduce((acc: Record<string, boolean>, id) => ({ ...acc, [id]: true }), {});
    setRowSelection(selection);
    setSelectedRowIds(ids);
    setChangeOrderSelectionActive(true);
    markStepComplete('selectCO');
    setSelectCOTriggered(true);
    toast.success(`Selected ${ids.length} change order row(s)!`);
  }, [rows, fieldHeadersMapping, setSelectedRowIds]);

  // Calculate totals row similar to original implementation
  const calculatedTotalsRow = useMemo(() => {
    const totals: RowData = { id: 'totals-row', description: 'Totals' };
    finalFieldsHeaders.forEach(({ field, headerName }) => {
      const mappedFieldKey = Object.keys(fieldHeadersMapping).find(key => fieldHeadersMapping[key] === headerName);
      if (!mappedFieldKey) return;

      if (headerName === 'Description') {
        (totals as any)[mappedFieldKey] = 'Totals';
        if (mappedFieldKey !== 'description') {
          (totals as any).description = '';
        }
        return;
      }

      // Subtotal rows are excluded from the grand total — they summarize rows already counted
      // above, so including them too would double-count.
      const total = filteredRows.reduce((acc: number, row: RowData) => {
        if (row.isSubtotal) return acc;
        const value = row[mappedFieldKey];
        const parsed = tryParseCurrencyLike(value);
        return parsed === null ? acc : acc + parsed;
      }, 0);
      (totals as any)[mappedFieldKey] = formatCurrency(total.toFixed(2), true, false);
    });
    return totals;
  }, [filteredRows, fieldHeadersMapping]);

  // Update totals callback effect similar to original
  useEffect(() => {
    const totalsOutput: Record<string, string> = {};
    finalFieldsHeaders.forEach(({ headerName }) => {
      const mappedFieldKey = Object.keys(fieldHeadersMapping).find(key => fieldHeadersMapping[key] === headerName);
      if (!mappedFieldKey) return;
      totalsOutput[headerName] = calculatedTotalsRow[mappedFieldKey];
    });
    onTotalsChange?.(totalsOutput);
  }, [calculatedTotalsRow, fieldHeadersMapping, onTotalsChange]);

  // Get cell class name for OCR styling
  const getCellClassName = (cell: MRT_Cell<RowData>) => {
    const rowId = cell.row.id;
    const field = cell.column.id;
    if (subtotalColumnInfoByRow[rowId]?.[field]?.mismatch) return 'subtotal-mismatch-cell';
    const ocrIssue = getOCRIssueForCell(rowId, field);
    const isFixed = isCellFixed(rowId, field);
    if (ocrIssue) return 'ocr-issue-cell';
    if (isFixed) return 'ocr-fixed-cell';
    return '';
  };

  // Add handleSaveCell function after getCellClassName
  const handleSaveCell = (cell: MRT_Cell<RowData>, value: any) => {
    clearUndoInfo();

    const updatedData: any = { ...cell.row.original };

    if (cell.column.id === 'division') {
      // Division handled separately
      return;
    } else {
      const columnId = cell.column.id;
      const isNumeric = isNumericAssignedColumn(columnId);
      const normalizedValue =
        isNumeric && (value === null || value === undefined || String(value).trim() === '')
          ? zeroDisplay
          : isNumeric && typeof value === 'string' && value.includes(':')
            ? value.replace(/:/g, '').trim()
            : value;
      (updatedData as any)[columnId] = normalizedValue;
    }

    setRows(prevRows =>
      prevRows.map((r, idx) => {
        // Prefer matching by id if it exists; otherwise fall back to the row index
        const isMatch = r.id ? r.id === cell.row.id : idx === cell.row.index;
        return isMatch ? { ...r, ...updatedData } : r;
      }),
    );
  };

  // Initialize Excel editing hook
  const excel = useExcelEditing<RowData>({
    tableInstanceRef,
    onSaveCell: handleSaveCell,
    excludedColumnIds: ['mrt-row-select', 'mrt-row-actions', 'division'],
    filteredRows,
    allFields,
  });

  // Column definitions with OCR styling and division support
  const columns = useMemo<MRT_ColumnDef<RowData, unknown>[]>(() => {
    const baseColumns: MRT_ColumnDef<RowData, unknown>[] = allFields
      // exclude the raw "division" field – we will add a custom Division column in the right place
      .filter(field => field !== 'division' && field !== 'id')
      .map(field => ({
        accessorKey: field,
        accessorFn(originalRow) {
          return originalRow[field];
        },
        header: fieldHeadersMapping[field] || field, // Keep the simple header for accessibility
        Header: () => {
          const mappedHeader = fieldHeadersMapping[field];
          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                cursor: 'pointer',
                width: '100%',
                '&:hover': {
                  opacity: 0.8,
                },
              }}
              onClick={(e: React.MouseEvent<HTMLDivElement>) => handleHeaderClick(field, e)}
            >
              <Typography variant='inherit' component='span' sx={{ flex: 1 }}>
                {mappedHeader || field}
              </Typography>
              {!mappedHeader && (
                <Typography
                  variant='caption'
                  sx={{
                    fontSize: '0.75rem',
                    color: 'rgba(255, 255, 255, 0.7)',
                    fontStyle: 'italic',
                  }}
                >
                  Click to map
                </Typography>
              )}
            </Box>
          );
        },
        size: 200,
        Cell: ({ cell }: { cell: MRT_Cell<RowData> }) => {
          const value = cell.getValue<any>();
          const row = cell.row.original;
          const isChangeOrder = row.isChangeOrder;
          const isSubtotal = row.isSubtotal;

          // Check if this is the description column
          const columnId = cell.column.id;
          const headerName = fieldHeadersMapping[columnId] || fieldHeadersMapping[field];
          const isDescriptionColumn = headerName === 'Description';

          if (isDescriptionColumn && isChangeOrder && value) {
            const tooltipText = `CO: ${String(value)}`;
            return (
              <TruncatedCell
                tooltipText={tooltipText}
                value={
                  <Box component='span' sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box component='span' sx={{ color: 'rgba(0, 0, 0, 0.6)', mr: 0.5 }}>
                      CO:{' '}
                    </Box>
                    <Box component='span'>{value}</Box>
                  </Box>
                }
              />
            );
          }

          if (isDescriptionColumn && isSubtotal && value) {
            const tooltipText = `Subtotal: ${String(value)}`;
            return (
              <TruncatedCell
                tooltipText={tooltipText}
                value={
                  <Box component='span' sx={{ display: 'flex', alignItems: 'center', fontWeight: 700 }}>
                    <Functions fontSize='small' sx={{ color: 'primary.main', mr: 0.5, fontSize: '1rem' }} />
                    <Box component='span'>{value}</Box>
                  </Box>
                }
              />
            );
          }

          // Numeric columns on a subtotal row show the sum of every row since the previous
          // subtotal (or the start), never the payapp's own raw value. If the payapp printed a
          // non-zero value in this same cell that meaningfully differs from that computed sum,
          // flag it — the mismatch styling comes from getCellClassName's 'subtotal-mismatch-cell'.
          if (isSubtotal && isNumericAssignedColumn(columnId)) {
            const info = subtotalColumnInfoByRow[row.id]?.[columnId];
            const computedSum = info?.computedSum ?? 0;
            const displayValue = formatCurrency(computedSum.toFixed(2), true);

            if (info?.mismatch) {
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', color: 'error.main', fontWeight: 700 }}>
                  <span>{displayValue}</span>
                  <IconTooltip
                    icon={<InfoOutlined fontSize='small' />}
                    color='error'
                    title={`Payapp value: ${formatCurrency((info.payappValue ?? 0).toFixed(2), true)}`}
                  />
                </Box>
              );
            }

            return <Box sx={{ fontWeight: 700, textAlign: 'right' }}>{displayValue}</Box>;
          }

          // If this column is mapped to a numeric PayApp field, never render an empty cell – assume 0.
          if (isNumericAssignedColumn(columnId) && (value === '' || value === null || value === undefined)) {
            return zeroDisplay;
          }

          if (typeof value === 'number') {
            return formatCurrency(value.toString(), true);
          }
          if (isDescriptionColumn) {
            const tooltipText = value == null ? '' : String(value);
            return <TruncatedCell tooltipText={tooltipText} value={value as React.ReactNode} />;
          }
          return value as React.ReactNode;
        },
        Footer: ({ table }: { table: MRT_TableInstance<RowData> }) => {
          const columns = table.getVisibleLeafColumns();
          const headerName = fieldHeadersMapping[field];

          // Check if this is the first Description column
          const isFirstDescriptionColumn =
            headerName === 'Description' &&
            columns
              .slice(
                0,
                columns.findIndex(col => col.id === field),
              )
              .every(col => fieldHeadersMapping[col.id] !== 'Description');

          return (
            <Stack>
              {/* Totals Row */}
              <Box
                sx={{
                  backgroundColor: theme => theme.palette.primary.main,
                  color: 'white !important',
                  fontWeight: 'bold',
                  padding: '8px 9px',
                  borderRight: '1px solid rgba(255, 255, 255, 0.15)',
                  minHeight: '52px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                }}
              >
                {isFirstDescriptionColumn
                  ? 'Totals'
                  : ['division', 'actions', 'mrt-row-select', 'mrt-row-actions'].includes(field)
                    ? ''
                    : calculatedTotalsRow[field] || ''}
              </Box>

              {/* Cover Page Values Row */}
              <Box
                sx={{
                  backgroundColor: 'white',
                  color: 'black',
                  fontWeight: 'medium',
                  padding: '8px 9px',
                  borderRight: '1px solid rgba(0, 0, 0, 0.15)',
                  minHeight: '52px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                }}
              >
                {isFirstDescriptionColumn
                  ? 'Cover Page Values'
                  : ['division', 'actions', 'mrt-row-select', 'mrt-row-actions'].includes(field)
                    ? ''
                    : (() => {
                        const coverField = tableHeaderToCoverFieldMap[headerName];
                        const coverFieldValue = state.coverPageValues[coverField] ?? '';
                        if (!coverFieldValue) return '';

                        return (
                          <Box display='flex' alignItems='center'>
                            {formatCurrency(coverFieldValue.toString(), true)}
                            {headerName &&
                              [
                                'Previous Application',
                                'Complete This Period',
                                'Stored This Period',
                                'Reallocation',
                                'Change Order',
                              ].includes(headerName) && (
                                <IconTooltip
                                  title={
                                    headerName === 'Previous Application' ||
                                    headerName === 'Complete This Period' ||
                                    headerName === 'Stored This Period'
                                      ? 'This value is the sum of the "Previous Application", "Complete This Period" and "Stored This Period" columns'
                                      : 'This value is the sum of the "Reallocation" and "Change Order" columns'
                                  }
                                  icon={<InfoOutlined fontSize='small' sx={{ ml: 0.5 }} />}
                                />
                              )}
                            {headerName === 'Balance to Complete' && (
                              <IconTooltip
                                title='This value is the sum of the "Retainage" and "Balance to Complete" columns'
                                icon={<InfoOutlined fontSize='small' sx={{ ml: 0.5 }} />}
                              />
                            )}
                          </Box>
                        );
                      })()}
              </Box>
            </Stack>
          );
        },
        muiTableHeadCellProps: {
          sx: theme => ({
            justifyContent: 'center',
            top: 0,
            backgroundColor: isFieldHeaderMapped(field) ? theme.palette.primary.dark : theme.palette.primary.main,
            color: '#ffffff',
            fontWeight: 'normal',
            padding: '8px 12px',
          }),
        },
        muiTableBodyCellProps: ({ cell }: { cell: MRT_Cell<RowData> }) => {
          const baseClass = getCellClassName(cell);
          const excelProps = excel.getBodyCellProps({ cell });
          return {
            ...excelProps,
            className: [baseClass, excelProps.className].filter(Boolean).join(' '),
          };
        },
        // All columns are normally editable since they're parsed from documents, except numeric
        // columns on a subtotal row — their displayed value is always a computed sum, not a raw
        // value, so editing them would have no effect.
        enableEditing: (row: MRT_Row<RowData>) => !(row.original.isSubtotal && isNumericAssignedColumn(field)),
        muiEditTextFieldProps: ({ cell }: { cell: MRT_Cell<RowData> }) => excel.getEditTextFieldProps({ cell }),
      }));

    // Add division column if we have a description column
    const hasDescriptionColumn = Object.values(fieldHeadersMapping).includes('Description');
    const descriptionFieldKey = Object.entries(fieldHeadersMapping).find(([_, value]) => value === 'Description')?.[0];

    if (hasDescriptionColumn && descriptionFieldKey) {
      const divisionColumn: MRT_ColumnDef<RowData, unknown> = {
        accessorKey: 'division',
        header: 'Division',
        enableHiding: true,
        size: 200,
        Cell: ({ row }: { row: MRT_Row<RowData> }) => {
          const division = row.original.division;
          return (
            <Box sx={{ cursor: 'pointer', color: 'blue' }}>
              <span>{division ? `${division.code} - ${division.name}` : <i>Click to edit</i>}</span>
            </Box>
          );
        },
        Edit: ({ row, table }: { row: MRT_Row<RowData>; table: MRT_TableInstance<RowData> }) => {
          const handleDivisionSelect = useCallback(
            (divisionId: string | null) => {
              handleDivisionChange(row.id, divisionId);
              table.setEditingCell(null); // Exit cell editing mode
            },
            [row.id, table],
          );

          return (
            <SelectDivisionFromGiven
              divisions={state.divisions}
              selectedDivision={row.original.division?.id ?? null}
              setSelectedDivision={handleDivisionSelect}
            />
          );
        },
        muiTableBodyCellProps: ({ cell }: { cell: MRT_Cell<RowData> }) => {
          const baseClass = getCellClassName(cell);
          const excelProps = excel.getBodyCellProps({ cell });
          return {
            ...excelProps,
            className: [baseClass, excelProps.className].filter(Boolean).join(' '),
          };
        },
        muiTableHeadCellProps: {
          sx: theme => ({
            justifyContent: 'center',
            backgroundColor: theme.palette.primary.dark,
            color: '#ffffff',
            fontWeight: 'normal',
            padding: '8px 12px',
            '&:hover': {
              backgroundColor: theme.palette.primary.dark,
            },
          }),
        },
        Footer: () => (
          <Stack>
            {/* Totals Row */}
            <Box
              sx={{
                backgroundColor: theme => theme.palette.primary.main,
                color: 'white !important',
                fontWeight: 'bold',
                padding: '8px 9px',
                borderRight: '1px solid rgba(255, 255, 255, 0.15)',
                minHeight: '52px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
              }}
            >
              {/* Empty content for totals row */}
            </Box>

            {/* Cover Page Values Row */}
            <Box
              sx={{
                backgroundColor: 'white',
                color: 'black',
                fontWeight: 'medium',
                padding: '8px 9px',
                borderRight: '1px solid rgba(0, 0, 0, 0.15)',
                minHeight: '52px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
              }}
            >
              {/* Empty content for cover page values row */}
            </Box>
          </Stack>
        ),
        enableEditing: true,
      };

      // Find the column that's mapped to Description
      const descIndex = baseColumns.findIndex(col => {
        const accessorKey = col.accessorKey as string;
        return fieldHeadersMapping[accessorKey] === 'Description';
      });

      if (descIndex !== -1) {
        baseColumns.splice(descIndex + 1, 0, divisionColumn);
      } else {
        baseColumns.push(divisionColumn);
      }

      // Insert any column mapped to "Change Order" in the right position (works after remount)
      const changeOrderFields = Object.keys(fieldHeadersMapping).filter(field =>
        (fieldHeadersMapping[field] ?? '').toLowerCase().includes('change order'),
      );

      // Process each Change Orders column
      changeOrderFields.forEach(changeOrderField => {
        const changeOrderColumn = baseColumns.find(col => col.accessorKey === changeOrderField);
        if (changeOrderColumn) {
          // Remove it from current position
          const currentIndex = baseColumns.findIndex(col => col.accessorKey === changeOrderField);
          if (currentIndex !== -1) {
            baseColumns.splice(currentIndex, 1);
          }

          // Find insertion point: after Original Budget or before Revised Budget
          let insertIndex = -1;

          // Prefer after any column mapped to a header containing "original budget"
          const originalBudgetIndex = baseColumns.findIndex(col => {
            const accessorKey = col.accessorKey as string;
            const header = (fieldHeadersMapping[accessorKey] ?? '').toLowerCase();
            return header.includes('original budget');
          });

          if (originalBudgetIndex !== -1) {
            insertIndex = originalBudgetIndex + 1;
          } else {
            // Otherwise place immediately before any column mapped to a header containing "revised budget"
            const revisedBudgetIndex = baseColumns.findIndex(col => {
              const accessorKey = col.accessorKey as string;
              const header = (fieldHeadersMapping[accessorKey] ?? '').toLowerCase();
              return header.includes('revised budget');
            });

            if (revisedBudgetIndex !== -1) {
              insertIndex = revisedBudgetIndex;
            }
          }

          // Insert at found position or append if not found
          if (insertIndex !== -1) {
            baseColumns.splice(insertIndex, 0, changeOrderColumn);
          } else {
            baseColumns.push(changeOrderColumn);
          }
        }
      });
    }

    return baseColumns;
  }, [
    allFields,
    fieldHeadersMapping,
    rows,
    state.divisions,
    calculatedTotalsRow,
    addedFields,
    excel,
    getCellClassName,
    isNumericAssignedColumn,
    subtotalColumnInfoByRow,
    zeroDisplay,
  ]);

  React.useEffect(() => {
    const newOrder = [
      'mrt-row-select', // pin the selection checkbox to the start
      ...columns.map(c => c.accessorKey as string), // followed by our data columns
      'mrt-row-actions', // and finally the actions column
    ];
    setColumnOrder(newOrder);
  }, [columns]);

  // Table instance with all features
  const mrtTable = useMaterialReactTable({
    columns,
    // While a subtotal is in range-edit mode, render the live in-progress reorder (previewRows)
    // instead of the last-committed order, so checking/unchecking a row visibly moves it right
    // away rather than only on Confirm.
    data: previewRows ?? filteredRows,
    initialState: {
      density: 'compact',
      pagination: { pageIndex: 0, pageSize: 250 },
      columnPinning: { left: ['mrt-row-select'], right: ['mrt-row-actions'] },
      showGlobalFilter: false,
    },
    // Show loading state when fixing headers
    state: {
      isLoading: state.fixingHeaders,
      columnVisibility,
      rowSelection: rangeEditSubtotalId ? rangeEditSelectionState : rowSelection,
      isFullScreen,
      columnOrder, // Pass the controlled columnOrder state to the table
      showGlobalFilter: false,
    },
    layoutMode: 'grid',
    enableRowSelection: (row: MRT_Row<RowData>) => (rangeEditSubtotalId ? rangeEditPoolIds.includes(row.original.id) : true),
    enableSelectAll: !rangeEditSubtotalId,
    enableMultiRowSelection: true,
    enableColumnResizing: true,
    enablePinning: true,
    enableColumnFilters: false,
    enableColumnActions: false,
    enableSorting: false,
    enableTopToolbar: isFullScreen,
    enableFullScreenToggle: false,
    enableDensityToggle: false,
    enableHiding: false,
    enableGlobalFilter: true,
    onIsFullScreenChange: setIsFullScreen,
    enableBottomToolbar: true,
    enableTableFooter: true,
    enableEditing: true,
    enableRowActions: true,
    enablePagination: true,
    muiSelectAllCheckboxProps: ({ table }) => buildSelectAllCheckboxProps(table),
    muiPaginationProps: {
      rowsPerPageOptions: [250, 500, 1000],
      showFirstButton: true,
      showLastButton: true,
    },
    editDisplayMode: 'cell',
    positionActionsColumn: 'last',
    getRowId: (row: RowData) => row.id,
    renderBottomToolbar: ({ table }) => (
      <Stack
        p={0}
        m={0}
        direction={'row'}
        alignItems={'center'}
        justifyContent={'flex-end'}
        sx={{
          width: '100%',
          backgroundColor: 'white',
          px: 2,
          py: 1,
          borderTop: '1px solid #eee',
          '& .MuiTablePagination-root': { m: 0, p: 0, minHeight: '32px' },
        }}
      >
        <MRT_TablePagination table={table} />
      </Stack>
    ),
    renderTopToolbarCustomActions: isFullScreen
      ? () => (
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'flex-end',
              px: 2,
              py: 0,
            }}
          >
            {renderHeader()}
          </Box>
        )
      : undefined,
    muiTableContainerProps: {
      ...excel.getContainerProps(),
      sx: {
        height: isFullScreen ? 'calc(100vh - 110px)' : 'auto',
        maxHeight: isFullScreen ? 'calc(100vh - 110px)' : '650px',
        overflow: 'auto',
        padding: 0,
        margin: 0,
        position: 'relative',
        '& .ocr-issue-cell': {
          backgroundColor: 'rgba(255, 193, 7, 0.2) !important',
        },
        '& .ocr-fixed-cell': {
          backgroundColor: 'rgba(40, 167, 69, 0.1) !important',
        },
        '& .subtotal-mismatch-cell': {
          backgroundColor: 'rgba(211, 47, 47, 0.12) !important',
        },
        ...excelSelectedCellSx,
        '& .ocr-issue-cell:hover': {
          backgroundColor: 'rgba(255, 193, 7, 0.3) !important',
        },
        '& .subtotal-mismatch-cell:hover': {
          backgroundColor: 'rgba(211, 47, 47, 0.2) !important',
        },
        // Keep header and footer in view while scrolling
        '& thead, & tfoot': {
          position: 'sticky',
          backgroundColor: 'white',
          zIndex: 1,
          margin: 0,
        },
        '& thead': {
          top: 0,
          margin: 0,
        },
        '& table': {
          margin: 0,
          borderSpacing: 0,
        },
        '& tfoot': {
          bottom: 0,
        },
        // Style for row separator hover area
        '& tbody tr': {
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '8px',
            zIndex: 5,
            cursor: 'pointer',
          },
        },
        // Override MRT's pinned cell alignment for actions column
        '& .css-1evnjx0-MuiTableCell-root': {
          textAlign: 'center !important',
          justifyContent: 'center !important',
        },
        // Override MUI's alignLeft class specifically for actions column
        '& .MuiTableCell-alignLeft[data-column-id="mrt-row-actions"]': {
          textAlign: 'center !important',
          justifyContent: 'center !important',
        },
      },
    },
    displayColumnDefOptions: {
      'mrt-row-select': {
        Footer: () => (
          <Stack>
            {/* Totals Row */}
            <Box
              sx={{
                backgroundColor: theme => theme.palette.primary.main,
                color: 'white !important',
                fontWeight: 'bold',
                padding: '8px 9px',
                borderRight: '1px solid rgba(255, 255, 255, 0.15)',
                minHeight: '52px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {/* Empty content for totals row */}
            </Box>

            {/* Cover Page Values Row */}
            <Box
              sx={{
                backgroundColor: 'white',
                color: 'black',
                fontWeight: 'medium',
                padding: '8px 9px',
                borderRight: '1px solid rgba(0, 0, 0, 0.15)',
                minHeight: '52px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {/* Empty content for cover page values row */}
            </Box>
          </Stack>
        ),
      },
      'mrt-row-actions': {
        header: '', // optional: remove default text
        size: 120, // adjust as needed for button width
        ...getActionsColumnStyling(),
        Header: ({ table }: { table: MRT_TableInstance<RowData> }) => (
          <Stack direction='row' spacing={1} alignItems='center' justifyContent='center'>
            <Tooltip title='Delete Selected Rows' arrow={false}>
              <span>
                <WhiteFab
                  size='small'
                  sx={{ width: 32, height: 32, minHeight: 32 }}
                  icon={<Delete fontSize='small' />}
                  textColor='error'
                  onClick={handleBulkDelete}
                  disabled={table.getSelectedRowModel().flatRows.length === 0}
                  loading={bulkDeleteInProgress}
                />
              </span>
            </Tooltip>
          </Stack>
        ),
        Footer: () => (
          <Stack>
            {/* Totals Row */}
            <Box
              sx={{
                backgroundColor: theme => theme.palette.primary.main,
                color: 'white !important',
                fontWeight: 'bold',
                padding: '8px 9px',
                borderRight: '1px solid rgba(255, 255, 255, 0.15)',
                minHeight: '52px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {/* Empty content for totals row */}
            </Box>

            {/* Cover Page Values Row */}
            <Box
              sx={{
                backgroundColor: 'white',
                color: 'black',
                fontWeight: 'medium',
                padding: '8px 9px',
                borderRight: '1px solid rgba(0, 0, 0, 0.15)',
                minHeight: '52px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {/* Empty content for cover page values row */}
            </Box>
          </Stack>
        ),
      },
    },
    muiTableHeadProps: {
      sx: {
        padding: 0,
        color: 'white',
        position: 'sticky',
        top: 0,
        marginTop: 0,
        zIndex: 2,
      },
    },
    muiTableHeadCellProps: {
      sx: theme => ({
        backgroundColor: theme.palette.primary.main,
        color: '#ffffff',
        justifyContent: 'center',
        padding: '8px 9px',
        border: 'none',
        top: 0,
        marginTop: 0,
        '&[data-pinned="true"]:not([role="columnheader"])::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: theme => theme.palette.primary.main,
          zIndex: 0,
        },
        '&[data-pinned="true"]:not([role="columnheader"])': {
          zIndex: 1,
        },
        '&:not(:last-of-type)::after': {
          content: '""',
          position: 'absolute',
          right: 0,
          top: '20%',
          height: '60%',
          width: '1px',
          backgroundColor: 'rgba(255, 255, 255, 0.15)',
          boxShadow: '0 0 4px rgba(255, 255, 255, 0.2)',
        },
      }),
    },
    muiTableBodyCellProps: {
      sx: {
        border: 'none',
        // Override alignment for actions column specifically
        '&[data-column-id="mrt-row-actions"]': {
          textAlign: 'center !important',
          '& .MuiStack-root': {
            justifyContent: 'center !important',
          },
        },
      },
    },
    muiTableBodyRowProps: ({ row }) => {
      const isHovered = hoveredRowIndex === row.index;
      const isSubtotal = row.original?.isSubtotal;
      const isActiveEditingSubtotal = rangeEditSubtotalId === row.original?.id;
      return {
        sx: {
          backgroundColor: isSubtotal ? SUBTOTAL_ROW_BACKGROUND_COLOR + ' !important' : row.index % 2 === 0 ? '#f9f9f9' : '#ffffff',
          fontWeight: isSubtotal ? 700 : 'normal',
          border: isActiveEditingSubtotal ? '2px solid #1976d2' : undefined,
          position: 'relative',
          '&:hover': {
            backgroundColor: isSubtotal ? SUBTOTAL_ROW_BACKGROUND_COLOR + ' !important' : row.index % 2 === 0 ? '#f5f5f5' : '#fafafa',
          },
          // Override MRT's default transparent selection color with a solid one
          '&.Mui-selected': {
            backgroundColor: '#e0f7fa', // Opaque light cyan for selected rows
            '&:hover': {
              backgroundColor: '#b2ebf2', // A slightly darker opaque color for hover
            },
          },
          // Show separator line when hovering over this row's top edge
          '&::before': isHovered
            ? {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '1.5px !important',
                backgroundColor: theme => theme.palette.primary.main,
                zIndex: 999,
                pointerEvents: 'none',
              }
            : {},
        },
        onMouseEnter: (e: React.MouseEvent) => {
          // Check if mouse is near the top edge of the row (separator area)
          const rect = e.currentTarget.getBoundingClientRect();
          const containerRect = tableContainerRef.current?.getBoundingClientRect();
          if (!containerRect) return;

          // Clear any pending timeout
          if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
            hoverTimeoutRef.current = null;
          }

          const mouseY = e.clientY;
          const rowTop = rect.top;
          const threshold = 8; // pixels from top edge

          if (mouseY - rowTop < threshold) {
            setHoveredRowIndex(row.index);
            setHoveredRowPosition({
              top: rowTop - containerRect.top,
            });
          }
        },
        onMouseMove: (e: React.MouseEvent) => {
          const rect = e.currentTarget.getBoundingClientRect();
          const containerRect = tableContainerRef.current?.getBoundingClientRect();
          if (!containerRect) return;

          // Clear any pending timeout
          if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
            hoverTimeoutRef.current = null;
          }

          const mouseY = e.clientY;
          const rowTop = rect.top;
          const threshold = 8; // pixels from top edge

          if (mouseY - rowTop < threshold) {
            setHoveredRowIndex(row.index);
            setHoveredRowPosition({
              top: rowTop - containerRect.top,
            });
          } else if (!isButtonHovered) {
            // Only clear if button is not being hovered
            setHoveredRowIndex(null);
            setHoveredRowPosition(null);
          }
        },
        onMouseLeave: () => {
          // Don't clear hover state if button is being hovered
          if (!isButtonHovered) {
            // Add a small delay to prevent glitching when moving to button
            hoverTimeoutRef.current = setTimeout(() => {
              if (!isButtonHovered) {
                setHoveredRowIndex(null);
                setHoveredRowPosition(null);
              }
            }, 100);
          }
        },
      };
    },
    muiTableFooterCellProps: {
      sx: {
        padding: 0,
        border: 'none',
        '& .MuiStack-root': {
          width: '100%',
        },
        '&[data-pinned="true"]': {
          position: 'sticky',
          backgroundColor: 'inherit',
          zIndex: 1,
        },
      },
    },
    muiTablePaperProps: {
      sx: {
        boxShadow: 'none',
        backgroundColor: 'white',
        height: 'auto',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      },
    },
    muiBottomToolbarProps: {
      sx: {
        overflow: 'visible',
        flexShrink: 0,
      },
    },
    onRowSelectionChange: handleRangeAwareRowSelectionChange,
    onColumnVisibilityChange: updatedVisibility => handleColumnVisibilityChange(updatedVisibility as MRT_VisibilityState),
    onColumnOrderChange: setColumnOrder, // Allow the user to drag-and-drop columns, and update our state
    renderRowActions: ({ row }) => {
      if (row.original.isSubtotal) {
        if (rangeEditSubtotalId === row.original.id) {
          return (
            <Stack direction='row' spacing={1}>
              <Tooltip title='Confirm Range'>
                <IconButton color='primary' size='small' onClick={handleConfirmRangeEdit}>
                  <Check fontSize='small' />
                </IconButton>
              </Tooltip>
              <Tooltip title='Cancel'>
                <IconButton color='error' size='small' onClick={handleCancelRangeEdit}>
                  <Close fontSize='small' />
                </IconButton>
              </Tooltip>
            </Stack>
          );
        }

        const otherRangeEditActive = !!rangeEditSubtotalId && rangeEditSubtotalId !== row.original.id;
        return (
          <Stack direction='row' spacing={1}>
            <Tooltip title='Edit Range'>
              <span>
                <IconButton color='primary' size='small' onClick={() => handleStartRangeEdit(row.id)} disabled={otherRangeEditActive}>
                  <Edit fontSize='small' />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title='Delete Row'>
              <span>
                <IconButton color='error' size='small' onClick={() => handleDeleteRow(row.id)} disabled={otherRangeEditActive}>
                  <Delete fontSize='small' />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        );
      }

      return (
        <Stack direction='row' spacing={1}>
          <Tooltip title='Delete Row'>
            <span>
              <IconButton color='error' size='small' onClick={() => handleDeleteRow(row.id)} disabled={!!rangeEditSubtotalId}>
                <Delete fontSize='small' />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      );
    },
  });
  tableInstanceRef.current = mrtTable;

  const scrollTableToBottom = useCallback(
    (behavior: ScrollBehavior = 'smooth') => {
      const container = excel.tableContainerRef.current;
      if (!container) return;
      container.scrollTo({ top: container.scrollHeight, behavior });
    },
    [excel.tableContainerRef],
  );

  const { pageIndex, pageSize } = mrtTable.getState().pagination;

  // After adding a row at the bottom, go to the last page (if needed) and scroll to the end.
  useEffect(() => {
    if (!pendingScrollToEndRef.current) return;

    // Pagination applies to the visible rows (filteredRows) since that's what we pass as table data.
    const totalVisibleRows = filteredRows.length;
    const lastPageIndex = Math.max(0, Math.ceil(totalVisibleRows / pageSize) - 1);

    if (pageIndex !== lastPageIndex) {
      pendingScrollToEndRef.current = false;
      pendingScrollToEndAfterPaginationRef.current = true;
      mrtTable.setPageIndex(lastPageIndex);
      return;
    }

    pendingScrollToEndRef.current = false;
    requestAnimationFrame(() => scrollTableToBottom('smooth'));
  }, [filteredRows.length, pageIndex, pageSize, scrollTableToBottom, mrtTable]);

  useEffect(() => {
    if (!pendingScrollToEndAfterPaginationRef.current) return;
    pendingScrollToEndAfterPaginationRef.current = false;
    requestAnimationFrame(() => scrollTableToBottom('smooth'));
  }, [pageIndex, scrollTableToBottom]);

  // If user clears selection manually, exit change order mode
  useEffect(() => {
    if (Object.keys(rowSelection).length === 0 && changeOrderSelectionActive) {
      setChangeOrderSelectionActive(false);
    }
  }, [rowSelection, changeOrderSelectionActive]);

  // Mark selected rows as change orders
  const markAsChangeOrder = useCallback(() => {
    const selectedIds = Object.keys(rowSelection).filter(id => rowSelection[id]);
    if (selectedIds.length === 0) {
      toast.info('Please select some rows first.');
      return;
    }

    setRows(prev =>
      prev.map(r => {
        if (selectedIds.includes(r.id)) {
          return { ...r, isChangeOrder: true };
        }
        return r;
      }),
    );

    toast.success(`Marked ${selectedIds.length} row(s) as change order(s).`);
  }, [rowSelection, setRows]);

  // Shared helper to move budget values to Change Orders for given rows
  const moveChangeOrders = useCallback(
    (selectedIds: string[]) => {
      // Find mapped keys - try Original Budget first, then Revised Budget
      const originalBudgetKey = Object.entries(fieldHeadersMapping).find(([, header]) => header === 'Original Budget')?.[0];
      const revisedBudgetKey = Object.entries(fieldHeadersMapping).find(([, header]) => header === 'Revised Budget')?.[0];
      const sourceBudgetKey = originalBudgetKey || revisedBudgetKey;

      if (!sourceBudgetKey) {
        toast.error('Please assign Original Budget or Revised Budget column first.');
        return;
      }

      // Ensure we have / create Change Orders column
      let changeOrdersKey = Object.entries(fieldHeadersMapping).find(([, h]) => h && h.toLowerCase().includes('change order'))?.[0];
      if (!changeOrdersKey) {
        const base = 'changeOrders';
        let idx = 1;
        let newKey = base;
        const existing = new Set([...Object.keys(rows[0] || {}), ...addedFields]);
        while (existing.has(newKey)) {
          newKey = `${base}_${idx++}`;
        }
        changeOrdersKey = newKey;

        const changeOrderHeader =
          finalFieldsHeaders.find(f => f.field.toLowerCase().includes('change') && f.headerName.toLowerCase().includes('change order'))
            ?.headerName || 'Change Order';

        setFieldHeadersMapping(prev => ({ ...prev, [newKey]: changeOrderHeader }));
        setAddedFields(prev => [...prev, newKey]);
        insertChangeOrderIntoColumnOrder(newKey);
      }

      const coKeyFinal = changeOrdersKey!;

      if (selectedIds.length === 0) {
        toast.info('Please select some rows first.');
        return;
      }

      setRows(prev =>
        prev.map(r => {
          const sourceVal = (r as any)[sourceBudgetKey!];
          const selected = selectedIds.includes(r.id);

          const moved = tryParseCurrencyLike(sourceVal) ?? 0;

          let newCOValue: any = selected ? moved : ((r as any)[coKeyFinal] ?? 0);
          if (typeof newCOValue === 'number') newCOValue = formatCurrency(newCOValue.toFixed(2), true, false);

          const updated: any = { ...r };
          updated[coKeyFinal] = newCOValue;
          if (selected) {
            // Only zero out Original Budget; if we pulled from Revised Budget, leave it as-is
            if (sourceBudgetKey === originalBudgetKey) {
              updated[sourceBudgetKey!] = formatCurrency('0.00', true, false);
            }
            updated.isChangeOrder = true; // Mark as change order when moving
          }
          return updated;
        }),
      );

      setChangeOrderSelectionActive(false);
      setRowSelection({});
      setSelectedRowIds([]);
      toast.success('Moved budget values to Change Orders for selected rows.');
    },
    [rows, fieldHeadersMapping, addedFields, finalFieldsHeaders],
  );

  // Undo handler – restores deleted rows to their original indices.
  const handleUndoDelete = () => {
    if (!undoInfo) return;
    setRows(prev => {
      const newRows = [...prev];
      undoInfo.rows.forEach((row, i) => {
        const insertIdx = undoInfo.indices[i] <= newRows.length ? undoInfo.indices[i] : newRows.length;
        newRows.splice(insertIdx, 0, row);
      });
      return newRows;
    });
    setUndoInfo(null);
    toast.success('Deletion undone');
  };

  // Merge columns handler
  const handleMergeColumns = useCallback(
    (sourceColumn: string, destinationColumn: string) => {
      if (!sourceColumn || !destinationColumn) {
        toast.error('Please select both source and destination columns.');
        return;
      }

      // Determine column types
      const isNumeric = getColumnTypeFromKey(sourceColumn, fieldHeadersMapping, rows) === 'number';

      clearUndoInfo();

      // Capture column names before deletion for toast message
      const sourceColumnName = fieldHeadersMapping[sourceColumn] || sourceColumn;
      const destinationColumnName = fieldHeadersMapping[destinationColumn] || destinationColumn;

      // Merge the values and remove source column from rows
      setRows(prev =>
        prev.map(row => {
          const sourceVal = (row as any)[sourceColumn];
          const destVal = (row as any)[destinationColumn];
          let mergedValue: any;

          if (isNumeric) {
            // For numbers: add them together
            const sourceNum = tryParseCurrencyLike(sourceVal) ?? 0;
            const destNum = tryParseCurrencyLike(destVal) ?? 0;
            const sum = sourceNum + destNum;
            // Format as currency if the destination was formatted
            if (typeof destVal === 'string' && destVal.includes('$')) {
              mergedValue = formatCurrency(sum.toFixed(2), true, false);
            } else {
              mergedValue = sum;
            }
          } else {
            // For strings: concatenate them
            const sourceStr = sourceVal != null ? String(sourceVal).trim() : '';
            const destStr = destVal != null ? String(destVal).trim() : '';

            if (sourceStr && destStr) {
              mergedValue = `${destStr} ${sourceStr}`;
            } else if (sourceStr) {
              mergedValue = sourceStr;
            } else if (destStr) {
              mergedValue = destStr;
            } else {
              mergedValue = '';
            }
          }

          // Create updated row with merged value and remove source column
          const updated = { ...row };
          (updated as any)[destinationColumn] = mergedValue;
          // Delete source column from row
          delete (updated as any)[sourceColumn];

          return updated;
        }),
      );

      // Remove source column from fieldHeadersMapping
      setFieldHeadersMapping(prev => {
        const updated = { ...prev };
        delete updated[sourceColumn];
        return updated;
      });

      // Remove source column from addedFields if it's there
      setAddedFields(prev => prev.filter(field => field !== sourceColumn));

      // Remove source column from columnOrder
      setColumnOrder(prev => prev.filter(col => col !== sourceColumn));

      toast.success(`Successfully merged "${sourceColumnName}" into "${destinationColumnName}" and removed source column.`);
    },
    [rows, fieldHeadersMapping],
  );

  // Wraps a guide-step button with an orange dot (active) or green tick (done)
  const withStepBadge = (key: GuideStepKey, button: React.ReactNode) => {
    const isActive = activeStepKey === key;
    const isDone = completedSteps.has(key);
    if (!isActive && !isDone) return <>{button}</>;
    return (
      <Box sx={{ position: 'relative', display: 'inline-block' }}>
        {button}
        {isActive && (
          <Box
            sx={{
              position: 'absolute',
              top: 2,
              right: 2,
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: '#f97316',
              border: '1.5px solid #fff',
              pointerEvents: 'none',
            }}
          />
        )}
        {isDone && !isActive && (
          <CheckCircleOutlined
            sx={{
              position: 'absolute',
              top: 0,
              right: 0,
              fontSize: 13,
              color: 'success.main',
              bgcolor: '#fff',
              borderRadius: '50%',
              pointerEvents: 'none',
            }}
          />
        )}
      </Box>
    );
  };

  const activeButtonSx = {
    borderBottom: '2px solid #f97316',
    borderRadius: '4px 4px 0 0',
    backgroundColor: 'rgba(249, 115, 22, 0.05)',
  };

  const renderHeader = () => (
    <Stack direction='column' spacing={0} sx={{ mb: isFullScreen ? 0 : 1, width: '100%' }}>
      <Stack direction='row' justifyContent='space-between' alignItems='flex-start' spacing={0}>
        <Stack direction='row' spacing={1} alignItems='center' sx={{ ml: 1, mt: 1.5 }}>
          <Typography component='span' variant='body2' sx={{ color: 'text.secondary' }}>
            Total Line Items: {filteredRows.length}
            {filteredRows.length !== rows.length && ` (${rows.length} total)`}
          </Typography>
          {/* Single selection indicator. The action buttons deliberately do NOT repeat the count in
              their labels — that made every button resize (and the whole row shift) on each click. */}
          {selectedCount > 0 && (
            <>
              <Box sx={{ width: '1px', height: 16, backgroundColor: 'divider', flexShrink: 0 }} />
              <Typography component='span' variant='body2' sx={{ color: 'primary.main', fontWeight: 600, whiteSpace: 'nowrap' }}>
                {selectedCount} row{selectedCount === 1 ? '' : 's'} selected
              </Typography>
            </>
          )}
          {(ocrIssuesCount > 0 || state.ocrFixedFields.size > 0) && (
            <Box sx={{ width: '1px', height: 16, backgroundColor: 'divider', flexShrink: 0 }} />
          )}
          {ocrIssuesCount > 0 && <LegendItem color='rgba(255, 193, 7, 0.2)' label={`Needs review (${ocrIssuesCount})`} />}
          {state.ocrFixedFields.size > 0 && <LegendItem color='#dcfce7' label={`Auto-fixed (${state.ocrFixedFields.size})`} />}
        </Stack>
        <Stack direction='row' spacing={0} alignItems='center'>
          {/* Action buttons — left to right: Fix Headers, Select/Mark Subtotals, Auto-Delete/Delete, Fix Currency, Select CO, Move CO, Mark CO.
              Subtotals deliberately sit BEFORE Auto-Delete: users sweep left-to-right, and reaching Auto-Delete
              first meant marking subtotals only after the rows had already been selected for deletion. */}
          {withStepBadge(
            'fixHeaders',
            <IconOverTextButton
              icon={<AutoFixHigh />}
              text='Fix Headers'
              tooltip={activeStepKey === 'fixHeaders' ? undefined : 'Automatically fix column headers using AI'}
              onClick={handleFixHeaders}
              disabled={state.fixingHeaders}
              loading={state.fixingHeaders}
              showHoverEffect={true}
              wrapInSpan={true}
              color='primary'
              sx={activeStepKey === 'fixHeaders' ? activeButtonSx : undefined}
            />,
          )}
          {undoInfo && (
            <IconOverTextButton
              icon={<Undo />}
              text='Undo Delete'
              tooltip='Undo Delete'
              onClick={handleUndoDelete}
              color='secondary'
              showHoverEffect={true}
            />
          )}
          {/* One button in two states, same shape as Auto-Delete → Delete: click once to select the
              subtotal candidates, click again in place to mark them. */}
          {selectedCount > 0
            ? withStepBadge(
                'markSubtotals',
                <IconOverTextButton
                  icon={<CheckCircleOutlined />}
                  text='Mark as Subtotal'
                  tooltip={
                    activeStepKey === 'markSubtotals'
                      ? undefined
                      : 'Mark the selected rows as subtotals so they are kept and never auto-deleted'
                  }
                  onClick={markAsSubtotal}
                  showHoverEffect={true}
                  wrapInSpan={true}
                  color='primary'
                  sx={activeStepKey === 'markSubtotals' ? activeButtonSx : undefined}
                />,
              )
            : withStepBadge(
                'selectSubtotals',
                <IconOverTextButton
                  icon={<Functions />}
                  text='Select Subtotals'
                  tooltip={
                    activeStepKey === 'selectSubtotals'
                      ? undefined
                      : 'Find rows with "total" in the Description column — click again to mark them as subtotals'
                  }
                  onClick={selectSubtotalRows}
                  showHoverEffect={true}
                  color='primary'
                  sx={activeStepKey === 'selectSubtotals' ? activeButtonSx : undefined}
                />,
              )}
          {selectedCount > 0
            ? withStepBadge(
                'deleteConfirm',
                <IconOverTextButton
                  icon={<Delete />}
                  text='Delete'
                  tooltip={activeStepKey === 'deleteConfirm' ? undefined : 'Delete selected rows'}
                  onClick={handleBulkDelete}
                  loading={bulkDeleteInProgress}
                  showHoverEffect={true}
                  color='error'
                  sx={
                    activeStepKey === 'deleteConfirm'
                      ? { ...activeButtonSx, borderBottomColor: 'error.main', backgroundColor: 'rgba(131,26,26,0.05)' }
                      : undefined
                  }
                />,
              )
            : withStepBadge(
                'autoDelete',
                <IconOverTextButton
                  icon={<AutoDeleteIcon />}
                  text='Auto-Delete'
                  tooltip={activeStepKey === 'autoDelete' ? undefined : 'Automatically select and delete empty or total rows'}
                  onClick={selectDeletableRows}
                  showHoverEffect={true}
                  color='primary'
                  sx={activeStepKey === 'autoDelete' ? activeButtonSx : undefined}
                />,
              )}
          {ocrIssuesCount > 0 &&
            onFixOCRIssues &&
            withStepBadge(
              'fixCurrency',
              <IconOverTextButton
                icon={<Build />}
                text={`Fix Currency (${ocrIssuesCount})`}
                tooltip={activeStepKey === 'fixCurrency' ? undefined : payappProcessTooltips.FixCurrencyFormat}
                onClick={() => {
                  onFixOCRIssues();
                  markStepComplete('fixCurrency');
                }}
                showHoverEffect={true}
                color='primary'
                sx={activeStepKey === 'fixCurrency' ? activeButtonSx : undefined}
              />,
            )}
          {hasChangeOrderRows &&
            withStepBadge(
              'selectCO',
              <IconOverTextButton
                icon={<BugReport />}
                text='Select CO'
                tooltip={activeStepKey === 'selectCO' ? undefined : 'Select Change Order rows'}
                onClick={selectChangeOrderRows}
                showHoverEffect={true}
                color='primary'
                sx={activeStepKey === 'selectCO' ? activeButtonSx : undefined}
              />,
            )}
          {withStepBadge(
            'moveCO',
            <IconOverTextButton
              icon={<Edit />}
              text='Move CO'
              tooltip={
                activeStepKey === 'moveCO'
                  ? undefined
                  : selectedCount === 0
                    ? 'Move amount from Original Budget to Change Orders. Select line items to enable this action.'
                    : 'Click to move amount from Original Budget to Change Orders for the selected line items'
              }
              onClick={() => {
                try {
                  const ids = Object.keys(rowSelection).filter(id => rowSelection[id]);
                  moveChangeOrders(ids);
                  markStepComplete('moveCO');
                  setSelectCOTriggered(false);
                } catch (e) {
                  handleError(e);
                }
              }}
              disabled={selectedCount === 0}
              showHoverEffect={true}
              wrapInSpan={true}
              color='primary'
              sx={activeStepKey === 'moveCO' ? activeButtonSx : undefined}
            />,
          )}
          <IconOverTextButton
            icon={<Edit />}
            text='Mark as CO'
            tooltip='Mark selected line items as change orders'
            onClick={markAsChangeOrder}
            disabled={selectedCount === 0}
            showHoverEffect={true}
            color='primary'
          />

          <ButtonDivider />

          <IconOverTextButton
            icon={<Add />}
            text='Add Row'
            tooltip='Add a new empty row at the bottom'
            onClick={() => handleAddNewRow()}
            showHoverEffect={true}
          />
          <IconOverTextButton
            icon={<MergeType />}
            text='Merge Columns'
            tooltip='Merge two columns together (add numbers or concatenate text)'
            onClick={() => setMergeDialogOpen(true)}
            showHoverEffect={true}
          />

          {/* Divider */}
          <ButtonDivider />

          {/* Utility buttons on the right */}
          <IconOverTextButton
            icon={<ViewColumnIcon />}
            text='Columns'
            tooltip='Manage Columns'
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => setColumnsMenuAnchorEl(e.currentTarget)}
            showHoverEffect={false}
            wrapInSpan={true}
          />

          <Badge badgeContent={getAppliedFiltersCount(filterModel, skippedFields)} color='primary' overlap='circular'>
            <IconOverTextButton
              icon={<FilterListIcon />}
              text='Filter'
              tooltip='Filter table rows'
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => setFilterAnchorEl(e.currentTarget)}
              showHoverEffect={false}
            />
          </Badge>
          <IconOverTextButton
            icon={<Search />}
            text='Search'
            tooltip='Search rows'
            onClick={() => setIsSearchOpen(!isSearchOpen)}
            sunken={isSearchOpen}
            showHoverEffect={true}
          />
          <Box
            sx={{
              ml: isSearchOpen ? 1 : 0,
              mr: 0,
              width: isSearchOpen ? '200px' : 0,
              transition: 'width 0.3s ease-in-out, opacity 0.3s ease-in-out, margin 0.3s ease-in-out',
              overflow: 'hidden',
              opacity: isSearchOpen ? 1 : 0,
            }}
          >
            <TextField
              placeholder='Search'
              size='medium'
              value={tableInstanceRef.current?.getState().globalFilter ?? ''}
              onChange={event => tableInstanceRef.current?.setGlobalFilter(event.target.value)}
              fullWidth
              InputProps={{ startAdornment: null }}
            />
          </Box>
          <IconOverTextButton
            icon={isFullScreen ? <FullscreenExit /> : <Fullscreen />}
            text={isFullScreen ? 'Exit Full' : 'Full Screen'}
            tooltip={isFullScreen ? 'Exit fullscreen mode' : 'Enter fullscreen mode'}
            onClick={() => setIsFullScreen(prev => !prev)}
            showHoverEffect={true}
          />
        </Stack>
      </Stack>
      {/* Info bar — spans full width below the buttons row */}
      {activeStepKey && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            px: 1.5,
            py: 0.6,
            borderRadius: '4px',
            backgroundColor: 'rgba(249, 115, 22, 0.08)',
            border: '1px solid rgba(249, 115, 22, 0.3)',
          }}
        >
          <InfoOutlined sx={{ fontSize: 14, color: '#f97316', flexShrink: 0 }} />
          <Typography variant='caption' sx={{ color: '#c2410c', lineHeight: 1.4 }}>
            {stepMessages[activeStepKey]}
          </Typography>
        </Box>
      )}
    </Stack>
  );

  return (
    <>
      {!isFullScreen && renderHeader()}
      <Menu anchorEl={filterAnchorEl} open={Boolean(filterAnchorEl)} onClose={() => setFilterAnchorEl(null)}>
        <FilterPanel columns={filterColumns} filterModel={filterModel} setFilterModel={setFilterModel} skippedFields={skippedFields} />
      </Menu>
      <Card
        ref={tableContainerRef}
        elevation={0}
        sx={{
          marginTop: 1,
          marginBottom: 2,
          display: 'flex',
          flexDirection: 'column',
          height: isFullScreen ? 'calc(100vh - 110px)' : '650px',
          paddingTop: isFullScreen ? 0 : undefined,
          position: 'relative',
          '& .MuiPaper-root': {
            margin: 0,
            padding: 0,
          },
          '& .MuiTableContainer-root': {
            margin: 0,
            padding: 0,
          },
        }}
      >
        <MaterialReactTable table={mrtTable} />
        {/* Floating plus button between rows */}
        {hoveredRowIndex !== null &&
          hoveredRowPosition &&
          (() => {
            const row = filteredRows[hoveredRowIndex];
            if (!row) return null;
            const originalIndex = rows.findIndex(r => r.id === row.id);
            if (originalIndex === -1) return null;

            return (
              <Box
                onMouseEnter={() => {
                  if (hoverTimeoutRef.current) {
                    clearTimeout(hoverTimeoutRef.current);
                    hoverTimeoutRef.current = null;
                  }
                  setIsButtonHovered(true);
                }}
                onMouseLeave={() => setIsButtonHovered(false)}
                sx={{
                  position: 'absolute',
                  right: '16px',
                  top: `${hoveredRowPosition.top}px`,
                  transform: 'translateY(-50%)',
                  zIndex: 1000,
                  pointerEvents: 'auto',
                }}
              >
                <IconButton
                  size='small'
                  onMouseEnter={() => {
                    if (hoverTimeoutRef.current) {
                      clearTimeout(hoverTimeoutRef.current);
                      hoverTimeoutRef.current = null;
                    }
                    setIsButtonHovered(true);
                  }}
                  onMouseLeave={() => setIsButtonHovered(false)}
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleAddNewRow(originalIndex);
                    setHoveredRowIndex(null);
                    setHoveredRowPosition(null);
                    setIsButtonHovered(false);
                  }}
                  sx={{
                    backgroundColor: theme => theme.palette.primary.main,
                    color: 'white !important',
                    width: 32,
                    height: 32,
                    '&:hover': {
                      backgroundColor: theme => theme.palette.primary.dark,
                      transform: 'scale(1.15)',
                    },
                    boxShadow: '0 4px 12px rgba(0,0,0,0.3), 0 2px 4px rgba(0,0,0,0.2)',
                    transition: 'all 0.2s ease-in-out',
                    '& svg': {
                      color: 'white !important',
                      fontSize: '20px',
                    },
                  }}
                >
                  <Add />
                </IconButton>
              </Box>
            );
          })()}
      </Card>
      {/* Popover outside Card to prevent clipping */}
      <Popover
        open={Boolean(popoverAnchor)}
        anchorEl={popoverAnchor}
        onClose={() => setPopoverAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            backgroundColor: 'background.paper',
            zIndex: 20000,
            width: 250,
            maxHeight: 400,
            overflowY: 'auto',
            mt: 1,
          },
        }}
      >
        <MenuItem
          key='unassign'
          selected={activeField ? !fieldHeadersMapping[activeField] : false}
          onClick={() => {
            if (activeField) {
              updateFieldHeadersMapping(activeField, '');
            }
            setPopoverAnchor(null);
          }}
        >
          Unassigned
        </MenuItem>
        {finalFieldsHeaders.map(option => {
          const isSelected = activeField ? fieldHeadersMapping[activeField] === option.headerName : false;
          // Only consider it mapped if there's an explicit assignment (key exists in mapping)
          // AND it's not the currently selected field
          const isMappedToAnotherColumn =
            Object.entries(fieldHeadersMapping).some(([key, value]) => value === option.headerName && key !== activeField) && !isSelected;

          return (
            <MenuItem
              key={option.field}
              selected={isSelected}
              onClick={() => {
                if (activeField) {
                  updateFieldHeadersMapping(activeField, option.headerName);
                }
                setPopoverAnchor(null);
              }}
              sx={{ display: 'flex', justifyContent: 'space-between' }}
            >
              <span>{option.headerName}</span>
              {isMappedToAnotherColumn && (
                <Typography variant='caption' sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                  (reassign)
                </Typography>
              )}
            </MenuItem>
          );
        })}
      </Popover>
      <CustomManageColumnsMenu
        anchorEl={columnsMenuAnchorEl}
        setAnchorEl={setColumnsMenuAnchorEl}
        table={mrtTable}
        title='Manage Columns'
      />
      <MergeColumnsDialog
        open={mergeDialogOpen}
        onClose={() => setMergeDialogOpen(false)}
        onMerge={handleMergeColumns}
        columns={allFields}
        fieldHeadersMapping={fieldHeadersMapping}
        rows={rows}
      />
    </>
  );
};

export default PayappTable;
